import { useEffect, FC, useMemo } from "react"
import { useQuery, useQueryClient } from "react-query"

import { Formik, Form, FormikValues, FieldArray} from 'formik'
import * as Yup from 'yup'

import { KTSVG } from "../../../../../../../_metronic/helpers"

import { DLMApplication } from "../../../_models"
import { submitNewDLMApplication, submitDLMPhase_1_2, editDLMApplication, getDLMTypeById } from "../../../_requests"
import { doFormSubmit, isPopulatedArray, processNoteValues } from "../../../../../../components/helpers"
import {
  StatusErrorMessage,
  FormikTextInput, FormikTextField, FormikFileUpload,
  FileUploaderResponse,
  FormikAcknowledgeFeeCheck,
  FormikNotesArea
} from "../../../../../../components/widgets"

import { AttachmentCard } from "../../../../attachments/AttachmentCard"

import { cloneDeep, findIndex, pickBy } from "lodash"
import { SubmitButtonRow } from "../../../../../../components/layout"
import { SCDFPlanNoSchema } from "../../../_schema"
import { DOCHourRatingDropdown } from "../../form"
import { IWebFileAttachment } from "../../../../applications"


interface IDOCIncreaseSiteModel {
  id: number,
  projectName: string
  projectLocation: string
  installerName: string
  bpNumber: string
  originalCoverageArea: number
  coverageArea: number
  unit:  string
  hourRating: string
  otherReference: string
  threshold: number
}

const docAddCoverageSiteSchema: any = Yup.object({
  projectName: Yup.string().trim().label('Project Name').required(),
  projectLocation: Yup.string().trim().label('Project Location').required(),
  installerName: Yup.string().trim().label('Installer Company').required(),
  bpNumber: SCDFPlanNoSchema,
  originalCoverageArea: Yup.number(),
  threshold: Yup.number(),
  coverageArea: Yup.number().label('NEW Total Coverage Area').required()
  .moreThan(Yup.ref('originalCoverageArea'), 'Must be more than original coverage area.')
  .max(Yup.ref('threshold'), ({max}) => `Threshold (${max}) exceeded.`),  
  unit: Yup.string().trim(),
  hourRating: Yup.string().trim().matches(/^(?!0$)(?!0\.0$)(-|\d+(\.\d{1})?)$/, "Select from options.").required().label('Hour Rating'),
  otherReference: Yup.string().trim().label('Other Reference'),  
});


const docAddCoverageSchema: any = Yup.object({
  siteA: docAddCoverageSiteSchema,
  otherAttachments: Yup.array(),
  comment: Yup.string().trim().label('Reason').required(),
  note: Yup.string().trim().label("Notes"),
  noteAttachments: Yup.array(),
  acknowledgeFee: Yup.bool().oneOf([true], 'Must be checked').label('Acknowledge Fee')
});

const initDOCSiteData: IDOCIncreaseSiteModel = {
  id: 0,
  projectName: '',
  projectLocation: '',
  installerName: '',
  bpNumber:  '',
  originalCoverageArea: 0,
  threshold: 0,
  coverageArea: 0,
  unit:  '',
  hourRating: '',
  otherReference: '',
}


interface IDOCIncreaseFormModel {
  siteA: IDOCIncreaseSiteModel
  unit: string
  coverageArea: number
  otherAttachments: Array<IWebFileAttachment>
  comment: string
  note: string
  noteAttachments: Array<IWebFileAttachment>
  acknowledgeFee: boolean
}


const initDOCAddCoverageFormValues: IDOCIncreaseFormModel = {
  siteA: { ...initDOCSiteData},
  unit: '',
  coverageArea: 0,
  otherAttachments: [],
  comment: '',
  note: '',
  noteAttachments: [],
  acknowledgeFee: false
}


const DOCINCREASE_SITEFIELDS = [
  'projectName',
  'projectLocation',
  'bpNumber',
  'installerName',
  'coverageArea',
  'unit',
  'hourRating',
  'otherReference',
];

type Props = {
  docId: number
  docNo: string
  appData: DLMApplication
  doClose: Function
  onSuccess: Function
  appId?: number
  resubmitMode? : string
}

const DLMDOCIncreaseModal:FC<Props> = ({docId, docNo, appData, doClose, onSuccess, appId, resubmitMode = ''}) => {

  const queryClient = useQueryClient();
  
  const { applicationCertificate, dlmType, labelSet } = appData;
  const certID = applicationCertificate && applicationCertificate?.id ? applicationCertificate.id : 0;
  const dlmTypeId = dlmType && dlmType?.id ? dlmType.id : 0;

  const { data: dlmtype_data } = useQuery(
    ['DLMTYPE-VIEW', dlmTypeId, certID ],
    () => getDLMTypeById(dlmTypeId, certID),
    {
      enabled: (dlmTypeId > 0 && certID > 0),
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  )

  const threshold = useMemo(() => {
    if (! (labelSet && labelSet?.id && dlmtype_data && dlmtype_data?.labelSets && isPopulatedArray(dlmtype_data.labelSets))) return 0;
    
    let _idx = findIndex(dlmtype_data.labelSets, {id : labelSet.id});

    return (_idx > -1) && dlmtype_data.labelSets[_idx]?.threshold ? dlmtype_data.labelSets[_idx].threshold : 0;

  }, [labelSet, dlmtype_data]);

  const { initValues, originalCoverageArea, unit, siteId } = useMemo(() => {

    let _init = cloneDeep(initDOCAddCoverageFormValues);
    let originalCoverageArea = 0;
    let unit = '';
    let siteId = 0;

    if (! appData) return { initValues: _init, originalCoverageArea, unit, siteId};


    const _site = {
      ...initDOCSiteData,
      ...pickBy(appData, (value, key) => (value && DOCINCREASE_SITEFIELDS.includes(key))),      
    };

    if (appData?.siteApplications && isPopulatedArray(appData.siteApplications)){
      let site = appData.siteApplications[0];
      Object.assign(_site, pickBy(site, (value, key) => (value && DOCINCREASE_SITEFIELDS.includes(key))));
      Object.assign(_init, pickBy(site, (value, key) => (value && ['unit', 'coverageArea'].includes(key))));
      siteId = site?.id ? site.id : 0;
    }

    if (appData?.otherAttachments && isPopulatedArray(appData.otherAttachments) ) {
      Object.assign(_init, { otherAttachments: appData.otherAttachments.map( file => ({id: file.id, name: file.fileName}))});
    }

    // set originalCoverage Area, unit
    originalCoverageArea = _init?.coverageArea ? _init.coverageArea : 0;
    unit =  _init.unit;

    if (resubmitMode && appData?.targetDOC) {
      originalCoverageArea = appData.targetDOC?.areaOfCoverage ? appData.targetDOC.areaOfCoverage : 0;
      unit = appData.targetDOC?.unit ? appData.targetDOC.unit : '';
    }

    Object.assign(_site, { 
      originalCoverageArea, 
      // max_value = threshold + original coverage
      threshold : threshold + originalCoverageArea,
    });

    Object.assign(_init, {
      siteA : { ..._site},      
    });

    return { initValues: _init, originalCoverageArea, unit, siteId};

  }, [appData, resubmitMode, threshold]);


  const submitFunc = useMemo(() => {
    switch(resubmitMode) {
      case 'resubmit':
        return submitDLMPhase_1_2;
      case 'edit':
        return editDLMApplication;
      default:
        return submitNewDLMApplication;
    }

  }, [resubmitMode]);

  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;
    const { comment, otherAttachments, siteA } = values;

    let _site = {
      ...pickBy(siteA, (value, key) => (value && DOCINCREASE_SITEFIELDS.includes(key))),
      coverageArea: parseFloat(`0${siteA.coverageArea}`),
    };

    if ( resubmitMode && siteId) {
      Object.assign(_site, { id : siteId});
    }

    let _payload = {
      targetDOC: docId,
      applicationType: "TYPE_DOCINCREASE",
      dlmWebPhase0SitesModel : [ _site ],
      coverageArea: parseFloat(`0${siteA.coverageArea}`),
    };

    if (comment) {
      Object.assign(_payload, { comment });
    }

    if (isPopulatedArray(otherAttachments)) {
      Object.assign(_payload, {otherAttachments: otherAttachments.map((o: any) => (o.id))});
    }

    if (appId) {
      Object.assign(_payload, { id: appId});
    }

    if (resubmitMode) {
      // @ts-ignore
      _payload = { application: _payload };
      processNoteValues(values, _payload)      
    }

    await doFormSubmit(submitFunc, _payload, setStatus, setSubmitting, (res) => {
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'doc-applications-list' } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      if (appId) {
        queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appId } ]);
      }
      if (res?.data) {
        onSuccess(res.data);
      }
    });

  }

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
  <>
  <div
    className='modal fade show d-block'
    id='kt_modal_doc_edit'
    role='dialog'
    tabIndex={-1}
    aria-modal='true'
  >
    <div className='modal-dialog modal-dialog-centered mw-500px'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2 className='fw-bolder'>Add Coverage to DOC : {`${docNo}`}</h2>
          <div
            className='btn btn-icon btn-sm btn-active-icon-primary'
            data-kt-applicants-modal-action='close'
            onClick={()=> { doClose()}}
            style={{cursor: 'pointer'}}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>
        <div className='modal-body scroll-y mx-1 mb-7'>
          <Formik
            initialValues={initValues}
            validationSchema={docAddCoverageSchema}
            enableReinitialize={true}
            validateOnMount={true}
            initialTouched={{
              projectName: true,
              projectLocation: true,
              installerName: true,
              hourRating: true,
              comment: true,
              unit: true,
            }}
            onSubmit={doSubmit}
          >
          {({status, values}) => (
            <Form id='kt_docaddcoverage_form' className='form' noValidate>
              <h4 className="text-center">{`Current Coverage Area :  ${originalCoverageArea} ${unit}`}</h4>
              <div className="row">
                <div className="p-4 border rounded">
                  <h4 className="text-center">Set New Total Coverage:</h4>
                  <div className='separator mb-2 opacity-75' />
                  <div className="row mb-1">
                    <FormikTextInput size="6" label="New TOTAL Coverage Area" name="siteA.coverageArea" decimals={3} required />
                    <FormikTextInput size="3" label="Unit" name="siteA.unit" readOnly />
                    <DOCHourRatingDropdown size="3" label="Hour Rating" name="siteA.hourRating" required={true} />
                  </div>
                  <div className='separator mb-2 opacity-75' />
                  <div className="row mb-1">
                    <FormikTextField size="12" label="Project Name" name="siteA.projectName" required rows={1} />
                  </div>
                  <div className="row mb-1">
                    <FormikTextField size="12" label="Project Location" name="siteA.projectLocation" required rows={2} />
                  </div>
                  <div className="row mb-1">
                    <FormikTextInput size="12" label="Installer Company" name="siteA.installerName" required />
                  </div>
                  <div className="row mb-1">
                    <FormikTextInput size="12" label="SCDF Building Plan No" name="siteA.bpNumber" />
                  </div>
                  <div className="row mb-1">
                    <FormikTextInput size="12" label="Other Reference Number (DSTA Number)" name="siteA.otherReference"/>
                  </div>

                </div>
              </div>

              <div className="mb-2 row">
                <FormikFileUpload size="6" label="Other Attachments"  name="otherAttachments" acceptFiles="ApplicantFileFormats" isFileArray={true} />
              </div>
              <FieldArray
                name="otherAttachments"
                render={arrayHelpers => (
                <div className="d-flex flex-row flex-wrap gap-2 mh-80px overflow-auto">
                { (values.otherAttachments && values.otherAttachments.length > 0) &&
                values.otherAttachments.map( (o: FileUploaderResponse, idx: number) => (
                  <AttachmentCard
                    key={`otherAttachment${idx}`}
                    className="border py-1"
                    idx={idx}
                    fileId={o.id}
                    fileName={o.name}
                    onRemove={(e: any) => {
                      e.stopPropagation();
                      const elem = e.target.closest('.attachment-card');
                      if (elem) {
                        const {idx} = elem.dataset;
                        arrayHelpers.remove(idx);
                      }
                    }}
                  />
                ))
                }
                </div>
                )}
              />
              <div className="row mb-2 mt-4">
                <FormikTextField size="12" label="Reason" name="comment" required={true} rows={2} />
              </div>
              { resubmitMode &&
              <>
              <div className='separator border-gray-200 my-4'></div>
              <FormikNotesArea attachLabel="Supporting Document/s" />
              </>
              }

              <FormikAcknowledgeFeeCheck dlmtype="DOC"/>

              <StatusErrorMessage status={status} />
              <SubmitButtonRow doClose={() => doClose()} />
            </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  </div>
  <div className='modal-backdrop fade show'></div>
  </>
  )
}

export {DLMDOCIncreaseModal}